import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";


export default function GatePage() {
  const navigate = useNavigate();
  const [bookingId, setBookingId] = useState("");
  const [lastName, setLastName] = useState("");
  const [destination, setDestination] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState("destination");

  const handleDestinationSelect = (dest) => {
    setDestination(dest);
    sessionStorage.setItem("selectedDestination", dest);
    setStep("credentials");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingId.trim() || !lastName.trim()) {
      toast.error("Please complete all fields");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/bookings/gate`, {
        booking_id: bookingId.trim(),
        last_name: lastName.trim()
      });

      sessionStorage.setItem("bookingData", JSON.stringify(response.data));
      navigate("/services");
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const createResponse = await axios.post(`${API}/bookings/create`, {
            booking_id: bookingId.trim(),
            last_name: lastName.trim(),
            destination: destination
          });

          sessionStorage.setItem("bookingData", JSON.stringify(createResponse.data.booking));
          
          toast.success(
            <div className="space-y-2">
              <p className="font-medium">Welcome</p>
              <p className="text-sm opacity-80">Your booking has been created.</p>
            </div>
          );
          
          navigate("/services");
        } catch (createError) {
          toast.error(createError.response?.data?.detail || "Unable to create booking");
        }
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Logo */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm">
        <div className="container-luxury py-5 flex items-center justify-between">
          {step === "credentials" ? (
            <button
              onClick={() => setStep("destination")}
              data-testid="back-button"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          
          <span className="brand-logo brand-logo-small">Charles Marlow</span>
          
          <div className="w-4" />
        </div>

        {/* Progress indicator */}
        <div className="container-luxury pb-5">
          <div className="flex items-center justify-center gap-2">
            <div className={`progress-dot ${step === "destination" ? "active" : "completed"}`} />
            <div className={`progress-dot ${step === "credentials" ? "active" : "pending"}`} />
            <div className="progress-dot pending" />
            <div className="progress-dot pending" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container-luxury pt-36 pb-20">
        {step === "destination" ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="heading-display text-2xl md:text-3xl text-center mb-4">
              Where are you staying?
            </h1>
            <p className="body-text text-center mb-16 max-w-sm mx-auto">
              Select your destination to begin planning your stay.
            </p>

            <div className="space-y-6 max-w-sm mx-auto">
              {[
                { id: "mallorca", name: "Mallorca", image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80" },
                { id: "ibiza", name: "Ibiza", image: "https://images.unsplash.com/photo-1534008757030-27299c4371b6?w=800&q=80" }
              ].map((dest) => (
                <motion.button
                  key={dest.id}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleDestinationSelect(dest.id)}
                  data-testid={`destination-${dest.id}`}
                  className="w-full relative rounded-2xl overflow-hidden aspect-[16/9] shadow-sm"
                >
                  <img
                    src={dest.image}
                    alt={dest.name}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ filter: "brightness(0.95) saturate(0.9)" }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="heading-display text-xl text-white text-left">
                      {dest.name}
                    </h3>
                  </div>
                </motion.button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-sm mx-auto">
              <p className="label-text text-center mb-4">
                {destination === "mallorca" ? "Mallorca" : "Ibiza"}
              </p>
              <h1 className="heading-display text-2xl md:text-3xl text-center mb-4">
                Your Booking
              </h1>
              <p className="body-text text-center mb-16">
                Enter your details to access or create your concierge plan.
              </p>

              <form onSubmit={handleSubmit} className="space-y-2">
                <div className="form-group">
                  <label htmlFor="bookingId" className="form-label">
                    Booking Reference
                  </label>
                  <input
                    id="bookingId"
                    data-testid="booking-id-input"
                    type="text"
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value.toUpperCase())}
                    placeholder="CM-12345"
                    className="form-input font-mono"
                    disabled={isLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    data-testid="last-name-input"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Smith"
                    className="form-input"
                    disabled={isLoading}
                  />
                </div>

                <div className="pt-6">
                  <button
                    type="submit"
                    data-testid="access-booking-btn"
                    disabled={isLoading}
                    className="btn-primary"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    ) : (
                      "Continue"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
